import { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setProfile } from '../store/userSlice';
import { updatePersonalDetails } from '../services/profile';
import {
  getPostAuthHomeOrQuestionnaire,
  readMedicalQuestionnaireCompletedFromStorage,
} from '../utils/signInRedirect';
import AuthPrimaryButton from './AuthPrimaryButton';
import AuthFormContainer from './AuthFormContainer';

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function getDaysInMonth(month, year) {
  if (!month || !year) return 31;
  const monthIndex = parseInt(month, 10) - 1;
  const yearNum = parseInt(year, 10);
  if (monthIndex === 1) {
    const isLeapYear = (yearNum % 4 === 0 && yearNum % 100 !== 0) || yearNum % 400 === 0;
    return isLeapYear ? 29 : 28;
  }
  const monthsWith30Days = [3, 5, 8, 10];
  if (monthsWith30Days.includes(monthIndex)) return 30;
  return 31;
}

function getDaysArray() {
  return Array.from({ length: 31 }, (_, i) => i + 1);
}

const years = Array.from(
  { length: 100 },
  (_, i) => new Date().getFullYear() - i
);

const validationSchema = Yup.object({
  gender: Yup.string()
    .oneOf(['male', 'female', 'other'], 'Select a valid gender')
    .required('Gender is required'),
  year: Yup.string().required('Year is required'),
  month: Yup.string().required('Month is required'),
  day: Yup.string().required('Day is required'),
  streetName: Yup.string().trim().required('Street name is required'),
  streetNumber: Yup.string().trim().required('Street number is required'),
  postalCode: Yup.string().trim().required('Postal code is required'),
  city: Yup.string().trim().required('City is required'),
  telephone: Yup.string()
    .trim()
    .matches(
      /^\+?[0-9]{10,15}$/,
      'Use format e.g. +31636200935 (optional +, then 10–15 digits)'
    )
    .required('Telephone number is required'),
});

const ChevronUpSvg = () => (
  <svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M5.18411 1.73387L1.2502 5.57318C1.09057 5.7299 1.00098 5.9419 1.00098 6.16288C1.00098 6.38386 1.09057 6.59586 1.2502 6.75258C1.32988 6.83098 1.42467 6.89321 1.52911 6.93567C1.63355 6.97814 1.74557 7 1.85872 7C1.97186 7 2.08388 6.97814 2.18832 6.93567C2.29276 6.89321 2.38755 6.83098 2.46723 6.75258L6.39256 2.9049C6.47224 2.8265 6.56703 2.76428 6.67147 2.72181C6.77591 2.67934 6.88793 2.65748 7.00107 2.65748C7.11422 2.65748 7.22624 2.67934 7.33068 2.72181C7.43512 2.76428 7.52991 2.8265 7.60959 2.9049L11.5349 6.75258C11.6952 6.91009 11.913 6.99901 12.1404 6.9998C12.3678 7.00058 12.5863 6.91316 12.7477 6.75676C12.909 6.60036 13.0002 6.3878 13.001 6.16584C13.0018 5.94387 12.9122 5.73069 12.7519 5.57318L8.81804 1.73387C8.33594 1.26395 7.68244 1 7.00107 1C6.31971 1 5.66621 1.26395 5.18411 1.73387Z"
      fill="#2D4343"
      stroke="#2D4343"
      strokeWidth="0.5"
    />
  </svg>
);

const ChevronDownSvg = () => (
  <svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8.81784 6.26613L12.7518 2.42682C12.9114 2.2701 13.001 2.0581 13.001 1.83712C13.001 1.61614 12.9114 1.40414 12.7518 1.24742C12.6721 1.16902 12.5773 1.10679 12.4728 1.06433C12.3684 1.02186 12.2564 1 12.1432 1C12.0301 1 11.9181 1.02186 11.8136 1.06433C11.7092 1.10679 11.6144 1.16902 11.5347 1.24742L7.60939 5.0951C7.52972 5.1735 7.43492 5.23572 7.33048 5.27819C7.22604 5.32066 7.11402 5.34252 7.00088 5.34252C6.88774 5.34252 6.77571 5.32066 6.67127 5.27819C6.56683 5.23572 6.47204 5.1735 6.39237 5.0951L2.46703 1.24742C2.30678 1.08991 2.08898 1.00099 1.86155 1.0002C1.63412 0.999418 1.41568 1.08684 1.25429 1.24324C1.09291 1.39964 1.00179 1.6122 1.00098 1.83416C1.00018 2.05613 1.08976 2.26931 1.25001 2.42682L5.18391 6.26613C5.66601 6.73605 6.31951 7 7.00088 7C7.68224 7 8.33575 6.73605 8.81784 6.26613Z"
      fill="#2D4343"
      stroke="#2D4343"
      strokeWidth="0.5"
    />
  </svg>
);

const DateOfBirthDropdownArrow = () => (
  <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#2D4343] flex flex-col gap-1.5">
    <ChevronUpSvg />
    <ChevronDownSvg />
  </div>
);

export default function PersonalInformationCard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const storedDateOfBirth = useSelector((state) => state.user?.dateOfBirth);
  const [gender, setGender] = useState('');
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');
  const [year, setYear] = useState('');
  const [streetName, setStreetName] = useState('');
  const [streetNumber, setStreetNumber] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [telephone, setTelephone] = useState('');
  const [dobError, setDobError] = useState('');
  const [serverErrors, setServerErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formik = useFormik({
    initialValues: {
      gender: '',
      year: '',
      month: '',
      day: '',
      streetName: '',
      streetNumber: '',
      postalCode: '',
      city: '',
      telephone: '',
    },
    validationSchema,
    onSubmit: () => {},
    validateOnBlur: false,
    validateOnChange: false,
  });

  useEffect(() => {
    const raw = storedDateOfBirth || (typeof localStorage !== 'undefined' ? localStorage.getItem('userDOB') : null);
    const match = String(raw || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return;
    const nextYear = String(parseInt(match[1], 10));
    const nextMonth = String(parseInt(match[2], 10));
    const nextDay = String(parseInt(match[3], 10));
    setYear(nextYear);
    setMonth(nextMonth);
    setDay(nextDay);
    formik.setFieldValue('year', nextYear);
    formik.setFieldValue('month', nextMonth);
    formik.setFieldValue('day', nextDay);
  }, [storedDateOfBirth]);

  const handleMonthChange = (e) => {
    const v = e.target.value;
    setMonth(v);
    formik.setFieldValue('month', v);
  };
  const handleDayChange = (e) => {
    const v = e.target.value;
    setDay(v);
    formik.setFieldValue('day', v);
  };
  const handleYearChange = (e) => {
    const v = e.target.value;
    setYear(v);
    formik.setFieldValue('year', v);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setServerErrors({});
    setDobError('');

    formik.setValues({
      gender,
      year,
      month,
      day,
      streetName,
      streetNumber,
      postalCode,
      city,
      telephone,
    });
    formik.setTouched({
      gender: true,
      year: true,
      month: true,
      day: true,
      streetName: true,
      streetNumber: true,
      postalCode: true,
      city: true,
      telephone: true,
    });

    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      setIsSubmitting(false);
      return;
    }

    const y = parseInt(year, 10);
    const m = parseInt(month, 10);
    const d = parseInt(day, 10);
    const isValidDOB =
      Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d) &&
      m >= 1 && m <= 12 && d >= 1 && d <= 31 &&
      d <= getDaysInMonth(m, y);
    if (!isValidDOB) {
      setDobError('Please enter a valid date of birth.');
      setIsSubmitting(false);
      return;
    }

    const dateOfBirthYyyyMmDd = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const genderCapitalized = gender ? gender.charAt(0).toUpperCase() + gender.slice(1) : '';

    const payload = {
      gender: genderCapitalized,
      date_of_birth: dateOfBirthYyyyMmDd,
      street_number: streetNumber.trim(),
      street_name: streetName.trim(),
      postal_code: postalCode.trim(),
      city: city.trim(),
      telephone_number: telephone.trim(),
    };

    try {
      const data = await updatePersonalDetails(payload);
      const newOnBoardingStep =
        data?.on_boarding_step != null ? String(data.on_boarding_step) : '';
      const newOnBoardingCompleted =
        typeof data?.on_boarding_completed === 'boolean' ? data.on_boarding_completed : false;

      dispatch(
        setProfile({
          gender: genderCapitalized,
          dateOfBirth: dateOfBirthYyyyMmDd,
          on_boarding_completed: newOnBoardingCompleted,
          on_boarding_step: newOnBoardingStep,
        })
      );
      try {
        localStorage.setItem('userGender', genderCapitalized);
        localStorage.setItem('userDOB', dateOfBirthYyyyMmDd);
        localStorage.setItem('on_boarding_completed', String(newOnBoardingCompleted));
        localStorage.setItem('on_boarding_step', newOnBoardingStep);
      } catch (_) {
        // ignore localStorage errors
      }

      const userRole = localStorage.getItem('userRole') || 'Patient';
      const viewAsRole = localStorage.getItem('supervisorViewAsRole');
      const redirectPath = getPostAuthHomeOrQuestionnaire(
        readMedicalQuestionnaireCompletedFromStorage(),
        userRole,
        viewAsRole
      );
      navigate(redirectPath);
    } catch (err) {
      setServerErrors({ submit: err?.response?.data?.message || err?.message || 'Something went wrong.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = 'w-full h-11 md:h-12 rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-4 md:px-5 outline-none font-poppins text-sm md:text-base text-[#2B3133] focus:border-[#119975]';
  const labelClass = 'text-[#889696] font-poppins text-sm font-medium';
  const errorClass = 'text-red-500 text-sm font-poppins mt-1';

  return (
    <AuthFormContainer>
      <div className="flex flex-col items-center gap-1 md:gap-2 w-full">
        <h1 className="text-[#2B3133] font-libreBaskerville text-2xl md:text-3xl leading-[1em] text-center">
          Personal Information
        </h1>
        <p className="text-[#889696] font-poppins text-sm md:text-base text-center">
          Please complete your personal details below.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-6">
        <div className="flex flex-col gap-4 w-full">
          {/* Gender */}
          <div className="flex flex-col gap-2 w-full relative">
            <label className={labelClass}>Gender</label>
            <div className="relative w-full h-11 md:h-12">
              <select
                value={gender}
                onChange={(e) => {
                  setGender(e.target.value);
                  formik.setFieldValue('gender', e.target.value);
                }}
                className={`${inputClass} pr-10 cursor-pointer appearance-none ${gender ? 'text-[#2B3133]' : 'text-[#A0AEC0]'}`}
                required
              >
                <option value="">Choose your gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
              {formik.touched.gender && formik.errors.gender && (
                <p className={`${errorClass} absolute top-full left-0 z-10`}>{formik.errors.gender}</p>
              )}
              <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none">
                <ChevronDownSvg />
              </div>
            </div>
          </div>

          {/* Date of birth */}
          <div className="flex flex-col gap-2 w-full relative">
            <label className={labelClass}>Date of birth</label>
            <div className="flex gap-2 md:gap-3 w-full">
              <div className="flex-1 relative min-w-0">
                <select
                  value={day}
                  onChange={handleDayChange}
                  className={`w-full h-11 md:h-12 rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-3 pr-8 outline-none font-poppins text-sm md:text-base cursor-pointer appearance-none focus:border-[#119975] ${day ? 'text-[#2B3133]' : 'text-[#A0AEC0]'}`}
                  required
                >
                  <option value="">Day</option>
                  {getDaysArray().map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <DateOfBirthDropdownArrow />
              </div>
              <div className="flex-1 relative min-w-0">
                <select
                  value={month}
                  onChange={handleMonthChange}
                  className={`w-full h-11 md:h-12 rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-3 pr-8 outline-none font-poppins text-sm md:text-base cursor-pointer appearance-none focus:border-[#119975] ${month ? 'text-[#2B3133]' : 'text-[#A0AEC0]'}`}
                  required
                >
                  <option value="">Month</option>
                  {months.map((mo, idx) => (
                    <option key={idx} value={idx + 1}>{mo}</option>
                  ))}
                </select>
                <DateOfBirthDropdownArrow />
              </div>
              <div className="flex-1 relative min-w-0">
                <select
                  value={year}
                  onChange={handleYearChange}
                  className={`w-full h-11 md:h-12 rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-3 pr-8 outline-none font-poppins text-sm md:text-base cursor-pointer appearance-none focus:border-[#119975] ${year ? 'text-[#2B3133]' : 'text-[#A0AEC0]'}`}
                  required
                >
                  <option value="">Year</option>
                  {years.map((yr) => (
                    <option key={yr} value={yr}>{yr}</option>
                  ))}
                </select>
                <DateOfBirthDropdownArrow />
              </div>
            </div>
            {(dobError || formik.errors.day || formik.errors.month || formik.errors.year) && (
              <p className={`${errorClass} absolute top-full left-0 z-10`}>
                {dobError || formik.errors.day || formik.errors.month || formik.errors.year}
              </p>
            )}
          </div>

          {/* Street Name & Street Number */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
            <div className="flex flex-col gap-2">
              <label className={labelClass}>Street Name</label>
              <input
                type="text"
                value={streetName}
                onChange={(e) => {
                  setStreetName(e.target.value);
                  formik.setFieldValue('streetName', e.target.value);
                }}
                className={inputClass}
                placeholder="Street Name"
                required
              />
              {formik.touched.streetName && formik.errors.streetName && (
                <p className={errorClass}>{formik.errors.streetName}</p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className={labelClass}>Street Number</label>
              <input
                type="text"
                value={streetNumber}
                onChange={(e) => {
                  setStreetNumber(e.target.value);
                  formik.setFieldValue('streetNumber', e.target.value);
                }}
                className={inputClass}
                placeholder="Street Number"
                required
              />
              {formik.touched.streetNumber && formik.errors.streetNumber && (
                <p className={errorClass}>{formik.errors.streetNumber}</p>
              )}
            </div>
          </div>

          {/* Postal Code & City */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
            <div className="flex flex-col gap-2">
              <label className={labelClass}>Postal code</label>
              <input
                type="text"
                value={postalCode}
                onChange={(e) => {
                  setPostalCode(e.target.value);
                  formik.setFieldValue('postalCode', e.target.value);
                }}
                className={inputClass}
                placeholder="Your postal code"
                required
              />
              {formik.touched.postalCode && formik.errors.postalCode && (
                <p className={errorClass}>{formik.errors.postalCode}</p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className={labelClass}>City</label>
              <input
                type="text"
                value={city}
                onChange={(e) => {
                  setCity(e.target.value);
                  formik.setFieldValue('city', e.target.value);
                }}
                className={inputClass}
                placeholder="City"
                required
              />
              {formik.touched.city && formik.errors.city && (
                <p className={errorClass}>{formik.errors.city}</p>
              )}
            </div>
          </div>

          {/* Telephone */}
          <div className="flex flex-col gap-2 w-full relative">
            <label className={labelClass}>Telephone number</label>
            <input
              type="tel"
              value={telephone}
              onChange={(e) => {
                setTelephone(e.target.value);
                formik.setFieldValue('telephone', e.target.value);
              }}
              className={inputClass}
              placeholder="e.g. +31249079870"
              required
            />
            {formik.touched.telephone && formik.errors.telephone && (
              <p className={`${errorClass} absolute top-full left-0 z-10`}>{formik.errors.telephone}</p>
            )}
          </div>

          {serverErrors.submit && (
            <p className="text-red-500 text-sm font-poppins w-full">{serverErrors.submit}</p>
          )}

          <AuthPrimaryButton
            type="submit"
            disabled={isSubmitting}
            className="mt-2"
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </AuthPrimaryButton>
        </div>
      </form>
    </AuthFormContainer>
  );
}
